import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, 
  Users, 
  Eye, 
  Globe, 
  Monitor, 
  Smartphone, 
  Tablet,
  TrendingUp,
  Clock,
  MousePointer,
  RefreshCw,
  ExternalLink,
  Activity,
  Copy,
  Code
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function Analytics() {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [domainStats, setDomainStats] = useState([]);
  const [realtimeData, setRealtimeData] = useState({ active_visitors: 0, domains: {} });
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [dateRange, setDateRange] = useState('30');
  const [refreshing, setRefreshing] = useState(false);

  // Get the production API URL for the tracking script
  const trackingApiUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  const trackingScript = `<script>
(function() {
  var API_URL = '${trackingApiUrl}/api/analytics/track';
  var visitorId = localStorage.getItem('_aurous_vid') || 
    'v_' + Math.random().toString(36).substr(2, 9);
  localStorage.setItem('_aurous_vid', visitorId);
  
  var sessionId = sessionStorage.getItem('_aurous_sid') || 
    's_' + Math.random().toString(36).substr(2, 9);
  sessionStorage.setItem('_aurous_sid', sessionId);
  
  fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domain: window.location.hostname,
      page_url: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer,
      visitor_id: visitorId,
      session_id: sessionId,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
  });
})();
</script>`;

  const copyTrackingScript = () => {
    navigator.clipboard.writeText(trackingScript);
    toast.success('Tracking script copied to clipboard!');
  };

  const domains = [
    { value: 'all', label: 'All Domains', color: 'bg-purple-500' },
    { value: 'aurousorbit.ai', label: 'AurousOrbit.ai', color: 'bg-indigo-500' },
    { value: 'myhrorbit.com', label: 'MyHR Orbit', color: 'bg-blue-500' },
    { value: 'myfinanceorbit.com', label: 'MyFinance Orbit', color: 'bg-green-500' },
  ];

  useEffect(() => {
    fetchAnalytics();
    fetchRealtimeData();
    
    // Refresh realtime data every 30 seconds
    const interval = setInterval(fetchRealtimeData, 30000);
    return () => clearInterval(interval);
  }, [selectedDomain, dateRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(dateRange) };
      if (selectedDomain !== 'all') {
        params.domain = selectedDomain;
      }
      
      const [summaryRes, domainsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params }),
        axios.get(`${API}/analytics/domains`, { params: { days: parseInt(dateRange) } })
      ]);
      
      setSummary(summaryRes.data);
      setDomainStats(domainsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRealtimeData = async () => {
    try {
      const res = await axios.get(`${API}/analytics/realtime`);
      setRealtimeData(res.data);
    } catch (error) {
      console.error('Error fetching realtime data:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    await fetchRealtimeData();
    setRefreshing(false);
    toast.success('Analytics data refreshed');
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", adminTheme.textPrimary(isDark))}>Analytics</h1>
          <p className={adminTheme.textSecondary(isDark)}>Track visitors across AurousOrbit products</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Realtime Indicator */}
          <div className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg",
            isDark ? "bg-green-900/30 border border-green-800" : "bg-green-50 border border-green-200"
          )}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
            </span>
            <span className={cn("text-sm font-medium", isDark ? "text-green-400" : "text-green-700")}>
              {realtimeData.active_visitors} active now
            </span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className={isDark ? "border-slate-700" : ""}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={selectedDomain} onValueChange={setSelectedDomain}>
          <SelectTrigger className={cn("w-48", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <Globe className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select domain" />
          </SelectTrigger>
          <SelectContent>
            {domains.map((domain) => (
              <SelectItem key={domain.value} value={domain.value}>
                <span className="flex items-center gap-2">
                  <span className={cn("w-2 h-2 rounded-full", domain.color)}></span>
                  {domain.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className={cn("w-40", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <Clock className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm font-medium", adminTheme.textSecondary(isDark))}>Page Views</p>
                <p className={cn("text-3xl font-bold mt-1", adminTheme.textPrimary(isDark))}>
                  {formatNumber(summary?.total_page_views || 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Eye className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm font-medium", adminTheme.textSecondary(isDark))}>Unique Visitors</p>
                <p className={cn("text-3xl font-bold mt-1", adminTheme.textPrimary(isDark))}>
                  {formatNumber(summary?.unique_visitors || 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/10">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm font-medium", adminTheme.textSecondary(isDark))}>Sessions</p>
                <p className={cn("text-3xl font-bold mt-1", adminTheme.textPrimary(isDark))}>
                  {formatNumber(summary?.unique_sessions || 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-green-500/10">
                <MousePointer className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm font-medium", adminTheme.textSecondary(isDark))}>Pages/Session</p>
                <p className={cn("text-3xl font-bold mt-1", adminTheme.textPrimary(isDark))}>
                  {summary?.avg_pages_per_session || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-orange-500/10">
                <TrendingUp className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Domain Breakdown */}
      <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
        <CardHeader>
          <CardTitle className={cn("flex items-center gap-2", adminTheme.textPrimary(isDark))}>
            <Globe className="w-5 h-5" />
            Traffic by Domain
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {domainStats.map((domain) => (
              <div
                key={domain.domain}
                className={cn(
                  "p-4 rounded-xl border transition-all hover:shadow-md",
                  isDark ? "bg-slate-800/50 border-slate-700" : "bg-slate-50 border-slate-200"
                )}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={cn(
                    "w-3 h-3 rounded-full",
                    domain.domain.includes('aurousorbit') ? 'bg-indigo-500' :
                    domain.domain.includes('myhrorbit') ? 'bg-blue-500' :
                    domain.domain.includes('myfinanceorbit') ? 'bg-green-500' :
                    'bg-purple-500'
                  )}></span>
                  <span className={cn("font-medium truncate", adminTheme.textPrimary(isDark))}>
                    {domain.domain}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className={adminTheme.textMuted(isDark)}>Page Views</p>
                    <p className={cn("font-semibold", adminTheme.textPrimary(isDark))}>
                      {formatNumber(domain.page_views)}
                    </p>
                  </div>
                  <div>
                    <p className={adminTheme.textMuted(isDark)}>Visitors</p>
                    <p className={cn("font-semibold", adminTheme.textPrimary(isDark))}>
                      {formatNumber(domain.unique_visitors)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  {Object.entries(domain.devices || {}).map(([device, count]) => (
                    count > 0 && (
                      <Badge key={device} variant="outline" className={cn("text-xs", isDark ? "border-slate-600" : "")}>
                        {getDeviceIcon(device)}
                        <span className="ml-1">{count}</span>
                      </Badge>
                    )
                  ))}
                </div>
              </div>
            ))}
            {domainStats.length === 0 && (
              <div className={cn("col-span-3 text-center py-8", adminTheme.textMuted(isDark))}>
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No traffic data yet. Install tracking script on your domains.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Pages */}
        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardHeader>
            <CardTitle className={cn("flex items-center gap-2", adminTheme.textPrimary(isDark))}>
              <BarChart3 className="w-5 h-5" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary?.top_pages?.slice(0, 8).map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                      isDark ? "bg-slate-700 text-slate-300" : "bg-slate-200 text-slate-600"
                    )}>
                      {index + 1}
                    </span>
                    <span className={cn("truncate text-sm", adminTheme.textPrimary(isDark))}>
                      {page.url}
                    </span>
                  </div>
                  <Badge variant="outline" className={isDark ? "border-slate-600" : ""}>
                    {formatNumber(page.views)}
                  </Badge>
                </div>
              ))}
              {(!summary?.top_pages || summary.top_pages.length === 0) && (
                <p className={cn("text-center py-4", adminTheme.textMuted(isDark))}>No page data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Device & Browser Breakdown */}
        <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
          <CardHeader>
            <CardTitle className={cn("flex items-center gap-2", adminTheme.textPrimary(isDark))}>
              <Monitor className="w-5 h-5" />
              Devices & Browsers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="devices">
              <TabsList className={cn("mb-4", isDark ? "bg-slate-800" : "")}>
                <TabsTrigger value="devices">Devices</TabsTrigger>
                <TabsTrigger value="browsers">Browsers</TabsTrigger>
              </TabsList>
              
              <TabsContent value="devices" className="space-y-3">
                {Object.entries(summary?.devices || {}).map(([device, count]) => {
                  const total = Object.values(summary?.devices || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={device} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className={cn("flex items-center gap-2 capitalize", adminTheme.textPrimary(isDark))}>
                          {getDeviceIcon(device)}
                          {device}
                        </span>
                        <span className={adminTheme.textMuted(isDark)}>{percentage}%</span>
                      </div>
                      <div className={cn("h-2 rounded-full", isDark ? "bg-slate-700" : "bg-slate-200")}>
                        <div
                          className={cn(
                            "h-full rounded-full",
                            device === 'desktop' ? 'bg-blue-500' :
                            device === 'mobile' ? 'bg-green-500' : 'bg-orange-500'
                          )}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
              
              <TabsContent value="browsers" className="space-y-3">
                {Object.entries(summary?.browsers || {}).map(([browser, count]) => {
                  const total = Object.values(summary?.browsers || {}).reduce((a, b) => a + b, 0);
                  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={browser} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className={adminTheme.textPrimary(isDark)}>{browser}</span>
                        <span className={adminTheme.textMuted(isDark)}>{percentage}%</span>
                      </div>
                      <div className={cn("h-2 rounded-full", isDark ? "bg-slate-700" : "bg-slate-200")}>
                        <div className="h-full rounded-full bg-purple-500" style={{ width: `${percentage}%` }} />
                      </div>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Tracking Script Info */}
      <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className={cn("flex items-center gap-2", adminTheme.textPrimary(isDark))}>
              <Code className="w-5 h-5" />
              Install Tracking Script
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={copyTrackingScript}
              className={isDark ? "border-slate-700" : ""}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy Script
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className={cn("text-sm mb-4", adminTheme.textSecondary(isDark))}>
            Add this script to your external domains (aurousorbit.ai, myhrorbit.com, myfinanceorbit.com) to start tracking visitors:
          </p>
          <pre className={cn(
            "p-4 rounded-lg text-xs overflow-x-auto",
            isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
          )}>
            {trackingScript}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
